/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [AUTO-SAVE] THE ARRANGEMENT BUILDER'S AUTOSAVE SWITCH — `ENABLE_AUTO_SAVE` IN THE ROOT `.env`.
 *
 *  A FRONT-END deployment fact, read by the WEB container and never by the API — `API_BASE_URL`'s
 *  shape exactly: `docker/web-entrypoint.sh` writes it into `/runtime-config.js` at start-up as
 *  `enableAutoSave`, so once an image HAS the switch, `docker compose up -d web` applies a change of
 *  its value with no rebuild. Under the Vite servers, which have no container to run that script,
 *  `vite.config.ts` answers `/runtime-config.js` from the same key — `pnpm dev` and `vite preview` both.
 *
 *  ⚠️ [AUTO-SAVE.1] AND THE FIRST ROLLOUT NEEDS `docker compose up -d --build web`. The switch is code
 *  in the BUNDLE and in the ENTRYPOINT, and both are baked into the image when it is built: an image
 *  built before this switch existed has no gate at all and writes no key, so a plain `up -d web`
 *  recreates the container onto code that never reads `.env`'s value — and the builder autosaves on
 *  every field left, whatever `.env` says. That is the LIKELIEST cause of the reported "still autosaves
 *  with it false" — likeliest, because the deployment itself could not be inspected.
 *
 *  ⚠️ A LEAF, WITH NO IMPORTS. `vite.config.ts` loads this file, and a config file is resolved by a
 *  loader that follows no `@/` alias and — under Vite's `configLoader: 'native'` — no extensionless
 *  import. Anything this module imported would break the dev server's start rather than a test.
 *
 *  ⚠️ NOT IN `.env` MEANS OFF — the user's instruction ("FALSE BY DEFAULT IF NOT SPECIFIED IN ENV").
 *  Every reader of the `.env` writes an EXPLICIT value: compose forwards `${ENABLE_AUTO_SAVE:-false}`
 *  to the web container, the entrypoint writes `false` for anything but `true`, and the Vite servers
 *  do the same (`autoSaveOfEnvText`).
 *
 *  ⚠️ [AUTO-SAVE.1] AND THE BROWSER IS OFF UNLESS IT IS TOLD `true` — REVERSING THE FIRST BUILD, which
 *  read a MISSING key as ON so the test suites kept their autosaving builder. MEASURED, in jsdom and in
 *  Chromium against the production bundle: with `enableAutoSave: false` nothing writes on any field
 *  change, and the SAME bundle autosaves on every one whenever that `false` does not arrive — a file with
 *  no key (`{}` from `web/public` on a static host, or `{ apiBaseUrl }` from a web container started
 *  without the key, or a proxy's copy of an older file) or no file at all (a `/runtime-config.js` that
 *  failed to load). Fail-open turned every break in the delivery chain into exactly the reported defect,
 *  silently, while the rest of the app worked. Now only an explicit `true` turns autosave on, so the
 *  test harnesses SAY so: `web/vitest.setup.ts` writes `enableAutoSave: true` before each test, and the
 *  browser suite hands its `vite preview` `ENABLE_AUTO_SAVE=true` (`e2e/playwright.config.ts`).
 *
 *  ⚠️ NO BUILD-TIME (`VITE_…`) HALF, UNLIKE `apiBaseUrl`. A value inlined by `vite build` reaches the
 *  bundle the browser suite serves and any image built on a machine that exported it, and would turn
 *  autosave off where nobody asked; the runtime file is the only channel.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** What a `.env` that does not name the key — or names it with anything but `true` — gets: OFF. */
export const AUTO_SAVE_WHEN_NOT_IN_ENV = false;

/**
 * ⚠️ [AUTO-SAVE.1] The browser's answer when `/runtime-config.js` carries NO key, or never loaded — OFF.
 *
 * DEFINED AS `AUTO_SAVE_WHEN_NOT_IN_ENV` RATHER THAN BESIDE IT, because they are one rule at two ends:
 * "not specified is off", whether the `.env` left the line out or the value was lost on its way to the
 * browser. It was `true` until a lost key was measured turning autosave back on under a `.env` saying
 * `false`; a harness that wants the autosaving builder writes `true`, as a deployment would.
 */
export const AUTO_SAVE_WITHOUT_RUNTIME_CONFIG = AUTO_SAVE_WHEN_NOT_IN_ENV;

/** The `/runtime-config.js` key, beside `apiBaseUrl`. */
export const AUTO_SAVE_CONFIG_KEY = 'enableAutoSave';

/**
 * The `.env` text → the switch. Exactly `true` or `false` — `EnvSchema`'s own boolean vocabulary
 * (`booleanEnv`), so one spelling works on every key in the file. Blank or absent is `'unset'`, and
 * anything else is `'invalid'`, which every caller REPORTS and then treats as not set — OFF, never a
 * refusal that takes the dashboard down. `docker/web-entrypoint.sh` applies the same rule in shell;
 * this is the Vite servers' copy of it, held by `autoSaveSetting.test.ts`.
 */
export const autoSaveSettingOf = (raw: string | undefined): boolean | 'unset' | 'invalid' => {
  if (raw === undefined || raw === '') return 'unset';
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return 'invalid';
};

/** The `.env` text → the value a `.env` reader writes: exactly `true` is ON, everything else OFF. */
export const autoSaveOfEnvText = (raw: string | undefined): boolean => {
  const setting = autoSaveSettingOf(raw);
  return typeof setting === 'boolean' ? setting : AUTO_SAVE_WHEN_NOT_IN_ENV;
};

/**
 * What the browser makes of `window.__PAMS_CONFIG__.enableAutoSave`. The Vite servers always emit a
 * JSON boolean, and the entrypoint does whenever its container is given the key — compose always gives
 * the web container one. ⚠️ [AUTO-SAVE.1] ONLY AN EXPLICIT ON TURNS AUTOSAVE ON — that boolean, or the same
 * word as a string from a hand-edited file — and everything else is `AUTO_SAVE_WITHOUT_RUNTIME_CONFIG`,
 * OFF: `false`, a misspelling, the key's absence and a page whose config never loaded alike.
 */
export const resolveAutoSave = (value: unknown): boolean =>
  value === true || value === 'true' ? true : AUTO_SAVE_WITHOUT_RUNTIME_CONFIG;

/**
 * The Vite servers' `/runtime-config.js` — `pnpm dev`'s and, [AUTO-SAVE.1], `vite preview`'s — always: set,
 * unset or invalid. It names `enableAutoSave` and nothing else: `apiBaseUrl` stays unset under `vite`, so
 * the app keeps falling through to `VITE_API_BASE_URL` and then to the same-origin `/api` proxy, exactly as
 * before.
 */
export const devRuntimeConfigScript = (enableAutoSave: boolean): string =>
  '/* Generated by the Vite server (web/vite.config.ts) from ENABLE_AUTO_SAVE. Do not edit. */\n' +
  `window.__PAMS_CONFIG__ = { ${AUTO_SAVE_CONFIG_KEY}: ${String(enableAutoSave)} };\n`;
